import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaFeatherAlt, FaCamera, FaGoogle } from 'react-icons/fa';
import { BlogData } from '../services/blogService';

interface Props {
    data: BlogData | null;
    loading: boolean;
}

// Sous-composant pour gérer l'erreur d'image individuellement
const BlogImage = ({ src, alt, credit, title }: { src: string, alt: string, credit: string, title: string }) => {
    const [hasError, setHasError] = useState(false);

    const openGoogleImages = () => {
        const query = encodeURIComponent(`${title} Quebec nature`);
        window.open(`https://www.google.com/search?tbm=isch&q=${query}`, '_blank');
    };

    if (hasError || !src) {
        return (
            <div className="w-full h-full bg-slate-100 flex flex-col items-center justify-center text-center p-6 border-4 border-white shadow-inner">
                <FaCamera className="text-4xl text-slate-300 mb-3" />
                <p className="text-xs text-slate-500 font-bold mb-3 uppercase tracking-wide">Image non disponible</p>
                <button 
                    onClick={openGoogleImages}
                    className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-slate-200 text-xs font-bold text-slate-600 hover:text-blue-600 hover:border-blue-300 transition"
                >
                    <FaGoogle /> Voir sur Google
                </button>
            </div>
        );
    }

    return (
        <div className="relative w-full h-full group">
            <img 
                src={src} 
                alt={alt}
                onError={() => setHasError(true)}
                className="w-full h-full object-cover transform group-hover:scale-105 transition duration-700"
            />
            {/* Crédit photo */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-white text-[10px] text-right">
                    © Photo par {credit} sur Unsplash
                </p>
            </div>
        </div>
    );
};

export default function RegionBlogSection({ data, loading }: Props) {
    // 1. AFFICHAGE PENDANT LE CHARGEMENT
    if (loading) {
        return (
            <div className="py-16 container mx-auto px-4 text-center animate-pulse bg-white">
                <div className="h-8 bg-slate-200 rounded w-1/2 mx-auto mb-6"></div>
                <div className="h-4 bg-slate-200 rounded w-2/3 mx-auto mb-10"></div>
                <div className="h-64 bg-slate-200 rounded-xl w-full max-w-4xl mx-auto mb-6"></div>
                <p className="text-slate-500 mt-6 font-medium flex items-center justify-center gap-2">
                    <FaFeatherAlt className="animate-bounce text-blue-500"/> 
                    L'Assistant IA rédige votre carnet de voyage...
                </p>
            </div>
        );
    }

    if (!data) return null;

    // 2. AFFICHAGE DE L'ARTICLE
    return (
        <section className="py-20 bg-white border-b border-slate-100">
            <div className="container mx-auto px-4 max-w-5xl">
                
                {/* EN-TÊTE DE L'ARTICLE */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }} 
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <span className="inline-block py-1 px-3 bg-yellow-100 text-yellow-800 text-xs font-bold uppercase tracking-wider rounded-full mb-4 shadow-sm">
                        Carnet d'Exploration Exclusif
                    </span>
                    <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6 leading-tight font-sans">
                        {data.title}
                    </h2>
                    <p className="text-xl text-slate-600 leading-relaxed font-light italic max-w-3xl mx-auto">
                        "{data.intro}"
                    </p>
                      <div className="flex justify-center items-center gap-3 text-xs text-slate-400 mt-6 border-t border-slate-100 pt-4 max-w-xs mx-auto">
                        <span className="flex items-center gap-1"><FaFeatherAlt /> Rédigé par IA Forfeo</span>
                        <span>•</span>
                        <span className="flex items-center gap-1"><FaCamera /> Photos Unsplash</span>
                    </div>
                </motion.div>

                {/* CORPS DE L'ARTICLE */}
                <div className="space-y-24">
                    {data.sections.map((section, index) => (
                        <motion.div 
                            key={index}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-10 items-center`}
                        >
                            {/* Colonne Texte */}
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold text-sm">
                                        {index + 1}
                                    </span>
                                    <h3 className="text-2xl font-bold text-slate-900">{section.title}</h3>
                                </div>
                                <p className="text-slate-700 text-lg leading-relaxed text-justify">
                                    {section.content}
                                </p>
                            </div>

                            {/* Colonne Photo (Avec gestion d'erreur) */}
                            <div className="flex-1 w-full">
                                <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3] border-4 border-white">
                                    <BlogImage 
                                        src={section.photoUrl} 
                                        alt={section.title} 
                                        credit={section.photoCredit} 
                                        title={section.title} 
                                    />
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* CONCLUSION */}
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }} 
                    whileInView={{ opacity: 1, scale: 1 }} 
                    viewport={{ once: true }}
                    className="mt-20 p-8 md:p-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl text-center border border-blue-100 shadow-sm"
                >
                    <p className="text-blue-900 font-medium text-lg md:text-xl italic">
                        "{data.conclusion}"
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
